import React from 'react';
import { X, Paperclip, Clock, Sparkles, Send, Loader2 } from 'lucide-react';

const EmailComposeModal = ({
  isOpen,
  onClose,
  // Form fields
  emailTo,
  setEmailTo,
  emailCc,
  setEmailCc,
  emailBcc,
  setEmailBcc,
  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  // Attachments
  composeAttachments,
  setComposeAttachments,
  // From account info
  senderEmail,
  // Actions
  onSend,
  onSchedule,
  onAIGenerate,
  // Loading states
  sendingEmail,
  generatingDraft
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-black border border-gray-400 rounded-xl w-full max-w-2xl mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">New Email</h3>
            <p className="text-gray-400 text-sm mt-1">
              Compose a new email from {senderEmail || 'your account'}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-900 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* To Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">To:</label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="recipient@example.com"
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="email-compose-to"
            />
          </div>

          {/* Cc Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Cc: (optional)</label>
            <input
              type="email"
              value={emailCc}
              onChange={(e) => setEmailCc(e.target.value)}
              placeholder="cc@example.com"
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="email-compose-cc"
            />
          </div>

          {/* Bcc Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Bcc: (optional)</label>
            <input
              type="email"
              value={emailBcc}
              onChange={(e) => setEmailBcc(e.target.value)}
              placeholder="bcc@example.com"
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="email-compose-bcc"
            />
          </div>

          {/* Subject Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Subject:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="email-compose-subject"
            />
          </div>

          {/* Body Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Message:</label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows="8"
              placeholder="Type your message..."
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              data-testid="email-compose-body"
            />
          </div>

          {/* Attached files */}
          {composeAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {composeAttachments.map((file, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm">
                  <Paperclip size={14} className="text-purple-400" />
                  <span className="text-gray-300 max-w-[150px] truncate">{file.name}</span>
                  <button
                    onClick={() => setComposeAttachments(prev => prev.filter((_, i) => i !== idx))}
                    className="text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <label className="p-2 hover:bg-gray-900 rounded-full transition-colors cursor-pointer" title="Attach file">
              <Paperclip size={20} className={composeAttachments.length > 0 ? 'text-purple-400' : 'text-gray-400'} />
              <input
                type="file"
                multiple
                className="hidden"
                data-testid="compose-attach-input"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setComposeAttachments(prev => [...prev, ...files]);
                  e.target.value = '';
                }}
              />
            </label>
            <button
              onClick={onSchedule}
              className="p-2 hover:bg-gray-900 rounded-full transition-colors"
              title="Schedule email"
              data-testid="compose-schedule-btn"
            >
              <Clock size={20} className="text-gray-400" />
            </button>
            <button
              onClick={onAIGenerate}
              disabled={generatingDraft}
              className="p-2 hover:bg-gray-900 rounded-full transition-colors disabled:opacity-50"
              title="Generate AI draft"
              data-testid="compose-ai-btn"
            >
              {generatingDraft ? (
                <Loader2 size={20} className="text-purple-400 animate-spin" />
              ) : (
                <Sparkles size={20} className="text-purple-400" />
              )}
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={onSend}
              disabled={sendingEmail || !emailTo.trim() || !emailSubject.trim()}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="compose-send-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposeModal;
